import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import moment from 'moment';
import { of, Subscription, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgFor } from '@angular/common';

import { SnackbarSaveDraftGuaranteeComponent } from '../../utils/snackbar-save-draft-guarantee.component';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { StepStatus, StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { User } from '../../../../shared/models/user';
import { ErrorService } from '../../../../shared/services/error.service';
import { LocationService } from '../../../../shared/services/location.service';
import { TranslationService } from '../../../../shared/services/translation.service';
import { UserService } from '../../../../shared/services/user.service';
import { Guarantee } from '../../../models/guarantee';
import { GuaranteeRequestService } from '../../../services/guarantee-request.service';
import { GuaranteeStatus } from '../../../models/guarantee-constants';
import { BocClient } from '../../../../shared/models/boc-client-transac';
import { LoadingSpinnerComponent } from '../../../../shared/components/utils/loading-spinner.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import {
	FeedbackErrorComponent
} from '../../../../shared/components/feedback-error/feedback-error.component';

interface ErrorObject {

	soldTo: boolean;
	name: boolean;

}
@Component({
	selector: 'copo-project-characteristics-solar',
	templateUrl: './project-characteristics-solar.component.html',
	styleUrl: './project-characteristics-solar.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		PageHeaderComponent,

		MatButtonModule,
		TranslateModule,
		MatIconModule,
		FormsModule,
		StepperComponent,

		MatFormFieldModule,

		MatInputModule,
		ReactiveFormsModule,

		MatSelectModule,
		NgFor,

		MatOptionModule,

		MatCheckboxModule,
		MatRadioModule,
		LoadingSpinnerComponent,
		FeedbackErrorComponent,
	]
})
export class ProjectCharacteristicsSolarComponent implements OnInit, OnDestroy {
	@ViewChild('soldToDiv', { read: ElementRef }) soldToDiv!: ElementRef<HTMLElement>;
	@ViewChild('nameDiv', { read: ElementRef }) nameDiv!: ElementRef<HTMLElement>;

	@Input() guaranteeResponse!: Guarantee;
	submitting = false;
	isLoading = true;
	soldTos: BocClient[] | undefined;
	guarantee!: Guarantee;
	user: User;
	readonly steps;

	onErrorMandatory = {
		soldTo: false,
		name: false,
	};

	powerFormControl!: FormControl<string>;
	magnelisTonnageFormControl!: FormControl<string>;
	mspCompanyNameFormControl!: FormControl<string>;
	epcCompanyNameFormControl!: FormControl<string>;
	soldToFormControl!: FormControl<string>;
	installationTypeFormControl!: FormControl<string>;
	newSolarPlantFormControl!: FormControl<boolean>;
	existingSolarPlantFormControl!: FormControl<boolean>;
	retrofittingSolarPlantFormControl!: FormControl<boolean>;
	projectNameFormControl!: FormControl<string>;
	private soldToSub!: Subscription;

	constructor(
		private readonly locationService: LocationService,
		private readonly translationService: TranslationService,
		private readonly userService: UserService,

		private readonly snackBar: MatSnackBar,
		private readonly router: Router,
		private readonly guaranteeService: GuaranteeRequestService,

		private readonly errorService: ErrorService,

	) {
		this.steps = [
			{
				label: this.translationService.getTR('guarantee.project.characteristics'),
				status: StepStatus.CURRENT,
			},
			{
				label: this.translationService.getTR('guarantee.project.environment'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('guarantee.type'),
				status: StepStatus.DISABLED,
			},
			{
				label: this.translationService.getTR('guarantee.summary'),
				status: StepStatus.DISABLED,
			}
		];
		this.user = this.userService.user;
	}

	ngOnInit() {
		this.errorService.resetErrorMsg();
		this.guarantee = this.guaranteeResponse;
		this.powerFormControl = new FormControl(this.guarantee.dataOnTheProjectSize, { nonNullable: true });
		this.magnelisTonnageFormControl = new FormControl(this.guarantee.magnelisTonnage, { nonNullable: true });
		this.mspCompanyNameFormControl = new FormControl(this.guarantee.mspCompanyName, { nonNullable: true });
		this.epcCompanyNameFormControl = new FormControl(this.guarantee.epcCompanyName, { nonNullable: true });

		this.installationTypeFormControl = new FormControl(this.guarantee.installationType, { nonNullable: true });

		this.newSolarPlantFormControl = new FormControl(
			this.guarantee.newSolarplantCompleteConstruction,
			{ nonNullable: true }
		);
		this.existingSolarPlantFormControl = new FormControl(
			this.guarantee.extensionExistingSolarplant,
			{ nonNullable: true }
		);
		this.retrofittingSolarPlantFormControl = new FormControl(this.guarantee.retrofitting, { nonNullable: true });
		this.soldToFormControl = new FormControl(
			this.guarantee.soldTo,
			{ validators: ctrl => this.onErrorMandatory.soldTo ? Validators.required(ctrl) : null, nonNullable: true }
		);
		this.projectNameFormControl = new FormControl(
			this.guarantee.name,
			{ validators: ctrl => this.onErrorMandatory.name ? Validators.required(ctrl) : null, nonNullable: true }
		);
		this.soldToSub = this.userService.getSoldTosTransac('PGNTE')
			.pipe(

				switchMap(businessIds => {
					if (businessIds.length === 0) {
						return of(undefined);
					}

					return this.userService.getBocs(
						['name', 'businessId'],
						businessIds.map(x => `businessId:${ x }`).join(' OR ')
					);
				})
			).subscribe({
				next: soldToList => {
					this.soldTos = soldToList?.results;
					if (this.soldTos && this.soldTos.length !== 0 && !this.guarantee.soldTo) {
						this.guarantee.soldTo = this.soldTos[0].businessId;
						this.soldToFormControl.setValue(this.guarantee.soldTo);
					}
				},
				complete: () => {
					this.isLoading = false;
				}

			});
	}

	ngOnDestroy() {
		this.soldToSub.unsubscribe();
	}

	goBack() {
		if (this.guarantee.id !== undefined) {
			this.locationService.go(-1);
		} else {
			this.locationService.go(-1, '../guarantee-requests/new/guarantee-type-selector');
		}
	}

	onErrorForm(elem: ElementRef, onError: keyof ErrorObject, event: Event) {
		(<HTMLElement> elem.nativeElement).scrollIntoView();
		this.onErrorMandatory[onError] = true;
		event.preventDefault();
	}

	isSoldToValid(event: Event) {
		if (this.guarantee.soldTo === '') {
			this.onErrorForm(this.soldToDiv, 'soldTo', event);
			this.soldToFormControl.markAsTouched();
			this.soldToFormControl.updateValueAndValidity();
			return false;
		}
		return true;
	}

	isNameValid(event: Event) {
		const trimmedName = this.guarantee.name.trim();
		if (trimmedName === '') {
			this.guarantee.name = '';
			this.projectNameFormControl.setValue('', { emitEvent: false });
			this.onErrorForm(this.nameDiv, 'name', event);
			this.projectNameFormControl.markAsTouched();
			this.projectNameFormControl.setErrors({ required: true });
			this.projectNameFormControl.updateValueAndValidity();
			return false;
		}
		this.guarantee.name = trimmedName;
		this.projectNameFormControl.setValue(trimmedName, { emitEvent: false });
		return true;
	}

	async isNameUnique(): Promise<boolean> {
		this.isLoading = true;
		try {
			return await this.guaranteeService.checkNameUniquenessOrFeedback(this.guarantee.name);
		} finally {
			this.isLoading = false;
		}
	}

	async goToNextStep(event: Event) {
		this.resetOnError();
		// check mandatory fields
		// we can't push if any mandatory are not filled

		if (!this.isNameValid(event) || !this.isSoldToValid(event) || await this.isNameUnique()) {
			return;
		}

		await this.router.navigate([
			this.guarantee.id !== undefined
				? `/guarantee-requests/edit/${ this.guarantee.id }/project-environment`
				: '/guarantee-requests/new/project-environment'
		], { replaceUrl: true });
	}

	async saveGuarantee(event: Event) {
		event.preventDefault();
		this.resetOnError();
		if (!this.isNameValid(event) || !this.isSoldToValid(event)) {
			return;
		}
		const now = moment().toISOString();
		const userName = `${ this.user.firstname } ${ this.user.lastname }`;
		const userId = this.user.id;
		if (this.guarantee.status !== GuaranteeStatus.DRAFT) {
			this.guarantee.requestDate = now;
		}
		this.guarantee.requesterName = userName;
		this.guarantee.requesterId = userId;

		this.submitting = true;
		try {
			await this.guaranteeService.saveGuarantee(this.guarantee);
		} catch (err) {
			console.error(err);
			this.guarantee.status = GuaranteeStatus.DRAFT;
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar'],
					data: true
				}
			);
			return;
		} finally {
			this.submitting = false;
		}
		if (this.guarantee.status === GuaranteeStatus.DRAFT) {
			await this.router.navigate(['/guarantee-requests/drafts'], { replaceUrl: true });
			this.snackBar.openFromComponent(
				SnackbarSaveDraftGuaranteeComponent,
				{
					duration: 5000,
					panelClass: ['copo-claim-draft-snackbar']
				}
			);
		}
	}

	resetOnError() {
		this.onErrorMandatory.soldTo = false;
		this.onErrorMandatory.name = false;
	}

	onInstallationTypeChange(event: MatRadioChange) {
		this.guarantee.installationType = String(event.value);
	}

	onNewSolarPlantCheckboxChange(checked: boolean) {
		this.guarantee.newSolarplantCompleteConstruction = checked;
	}

	onExistingSolarPlantCheckboxChange(checked: boolean) {
		this.guarantee.extensionExistingSolarplant = checked;
	}

	onRetrofittingSolarPlantCheckboxChange(checked: boolean) {
		this.guarantee.retrofitting = checked;
	}

	epcCompanyNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.epcCompanyName = target.value;
	}

	mspCompanyNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.mspCompanyName = target.value;
	}

	magnelisTonnageNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.magnelisTonnage = target.value;
	}

	powerNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.dataOnTheProjectSize = target.value;
	}

	soldToChanged(event: MatSelectChange) {
		this.guarantee.soldTo = <string> event.value;
		this.errorService.resetErrorMsg();
	}

	projectNameChanged(event: Event) {
		const target = <HTMLInputElement> event.target;
		this.guarantee.name = target.value.trim();
		this.projectNameFormControl.setValue(this.guarantee.name, { emitEvent: false });
		this.errorService.resetErrorMsg();
	}
}
